import { memo, useState } from 'react'
import cn from 'classnames'

import { copy, LunyaMascot } from '~/modules/lunya'
import { ANALYTICS_EVENTS, useAnalytics, useTrackOnMount } from '~/shared/lib/analytics'
import { Avatar, Button, Icon, LoadingState, Screen, Spacer, Text, VStack } from '~/shared/ui'

import { AcceptTermsMutation, InvitePreviewQuery, UpdateUserMutation } from '../../api'
import { GenderChips, NameInput, TermsAcceptNote } from '../../components'
import { useGenderGuess } from '../../hooks'
import type { Gender } from '../../types'
import {
  MASCOT_SIZE,
  STAR_LEFT_PERCENT,
  STAR_RIGHT_PERCENT,
  STAR_SIZE,
  STARS_BLOCK_HEIGHT,
  THREAD_DASH,
  THREAD_LINE,
  THREAD_VIEWBOX,
} from './config'
import type { InviteEntryWidgetProps } from './types'

import styles from './InviteEntryWidget.module.scss'

/** B0 · Вход по приглашению — заменяет A1 (спека 02 §B0). Далее флоу продолжается как A2 → A3. */
export const InviteEntryWidget = memo(function InviteEntryWidget(props: InviteEntryWidgetProps) {
  const { inviteToken, initialName, onNext } = props

  useTrackOnMount(ANALYTICS_EVENTS.onbB0Shown())
  const { track } = useAnalytics()

  const { data: preview, isLoading } = InvitePreviewQuery.useQuery({ token: inviteToken })
  const [name, setName] = useState(initialName)
  const [manualGender, setManualGender] = useState<Gender | null>(null)
  const [updateUser, { isPending }] = UpdateUserMutation.useMutation()
  const [acceptTerms] = AcceptTermsMutation.useMutation()

  const inviterName = preview?.inviterName ?? ''
  // Пока превью едет — пола нет, и это ровно тот же случай, что «пол не указан»: Луня говорит
  // о пригласившем без рода, а не подставляет женский.
  const inviterGender = preview?.inviterGender ?? null

  // Пол самого пришедшего. B0 заменяет A1 целиком, а спрашивал пол только A1 — поэтому у всех,
  // кто вошёл по приглашению, `gender` оставался `null` навсегда, и КАЖДАЯ фраза о них до конца
  // жизни аккаунта шла в безродной форме (правка основателя: род должен браться там, где он
  // известен). Механика ровно та же, что в A1: молча угадываем по имени, спрашиваем только
  // при неуверенном угадывании.
  const guessedGender = useGenderGuess(name)
  const isAmbiguous = name.trim() !== '' && guessedGender === null
  const ownGender = guessedGender ?? manualGender

  const trimmedName = name.trim()
  const canSubmit = !isLoading && trimmedName !== '' && (!isAmbiguous || ownGender !== null)

  const handleSubmit = async () => {
    if (!canSubmit) {
      return
    }
    // B0 заменяет A1 целиком, а значит и строку акцепта под кнопкой: пришедшие по приглашению
    // первого экрана регистрации не видят никогда. Забыть об этом здесь означало бы, что
    // ровно у виральной половины аудитории договор не заключён (docs/legal.md §13.3).
    track(ANALYTICS_EVENTS.termsAccepted({ source: 'onboarding' }))
    void acceptTerms({ source: 'onboarding' })

    await updateUser({ firstName: trimmedName, gender: ownGender ?? undefined })
    onNext({ name: trimmedName, gender: ownGender, inviterName, inviterGender })
  }

  return (
    <Screen background='FLOW' hasStickyCta>
      <Screen.Content>
        <div className={styles.root}>
          <div className={styles.starsBlock} style={{ height: STARS_BLOCK_HEIGHT }}>
            <div className={styles.star} style={{ left: `${STAR_LEFT_PERCENT}%` }}>
              <Avatar size={STAR_SIZE} name={inviterName} glow='ME' />
            </div>
            <div className={styles.star} style={{ left: `${STAR_RIGHT_PERCENT}%` }}>
              <div className={styles.starFlat} style={{ width: STAR_SIZE, height: STAR_SIZE }}>
                <Icon name='sparkle' size={22} />
              </div>
            </div>
            <svg viewBox={THREAD_VIEWBOX} preserveAspectRatio='none' className={styles.thread}>
              <line
                x1={THREAD_LINE.x1}
                y1={THREAD_LINE.y1}
                x2={THREAD_LINE.x2}
                y2={THREAD_LINE.y2}
                stroke='var(--color-accent)'
                strokeWidth={2.4}
                strokeLinecap='round'
                strokeDasharray={THREAD_DASH}
                opacity={0.8}
              />
            </svg>
          </div>

          <div className={styles.mascot}>
            <LunyaMascot pose='CALLING' size={MASCOT_SIZE} />
          </div>

          {isLoading ? (
            <LoadingState size='S' />
          ) : (
            <VStack gap='SMALL_M' align='CENTER'>
              <Text variant='H1' align='CENTER'>
                {copy.onb.b0.title({ inviter: inviterName })}
              </Text>
              <Text variant='BODY_S' color='SECONDARY' align='CENTER'>
                {copy.onb.b0.subtitle({ inviter: inviterName, gender: inviterGender })}
              </Text>
            </VStack>
          )}

          <Spacer grow />

          <VStack gap='SMALL_M'>
            <NameInput value={name} onChange={setName} />
            {/* Место под вопрос о поле зарезервировано всегда — как в A1: иначе появление
                чипов толкает поле имени вверх, и экран прыгает при вводе. */}
            <div
              className={cn(styles.genderSlot, isAmbiguous && styles.genderSlotVisible)}
              aria-hidden={!isAmbiguous}
            >
              <GenderChips value={manualGender} onChange={setManualGender} />
            </div>
          </VStack>
        </div>
      </Screen.Content>
      <Screen.Footer>
        <VStack gap='SMALL_M'>
          <Button
            variant='PRIMARY'
            width='FULL'
            text={copy.onb.b0.cta({ inviter: inviterName })}
            isDisabled={!canSubmit}
            isLoading={isPending}
            onPress={handleSubmit}
          />
          {/* Та же строка акцепта, что на A1: этот экран для входа B и есть первый. */}
          <TermsAcceptNote />
        </VStack>
      </Screen.Footer>
    </Screen>
  )
})
